
import { ScrollArea } from "@/components/ui/scroll-area";
import { useEffect, useRef, useState } from "react";
import AttachmentViewer from "./AttachmentViewer";
import { Email } from "@/types/email";

interface EmailViewerProps {
  email: Email | null;
  hideSubject?: boolean;
}

const EmailViewer = ({ email, hideSubject = false }: EmailViewerProps) => {
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (email?.htmlContent && contentRef.current) {
      // Debug logging
      console.log("Processing email HTML content for email:", email.id);
      
      // Monitor for style changes
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
            console.warn("Style mutation detected:", {
              element: mutation.target,
              oldValue: mutation.oldValue,
              newValue: (mutation.target as HTMLElement).style.cssText
            });
          }
        });
      });

      // Observe the email content wrapper
      observer.observe(contentRef.current, {
        attributes: true,
        childList: true,
        subtree: true,
        attributeOldValue: true
      });

      // Cleanup
      return () => observer.disconnect();
    }
  }, [email?.id, email?.htmlContent]);

  if (!email) {
    return (
      <div className="text-center py-12 text-gray-500">
        Select an email to view its contents
      </div>
    );
  }

  // Function to sanitize and process HTML content
  const processHtmlContent = (html: string) => {
    console.log("Processing HTML content for email:", email.id);
    
    // Ensure the content has proper charset meta
    let processedHtml = html;
    
    // Add meta charset if not present
    if (!processedHtml.includes('<meta charset="utf-8"') && 
        !processedHtml.includes('<meta charset="UTF-8"')) {
      processedHtml = `<meta charset="utf-8">${processedHtml}`;
    }
    
    // Replace data-image-url with actual image src if present
    processedHtml = processedHtml
      .replace(/data-image-url="([^"]+)"/g, 'src="$1"')
      // Replace any CID references with actual image data if available
      .replace(/src="cid:([^"]+)"/g, (match, cid) => {
        // Here you would ideally map the CID to actual image data
        console.log("Found CID reference:", cid);
        return match;
      });

    // Remove any <style> tags from the email content
    processedHtml = processedHtml.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
    
    // Remove any inline styles that might affect layout
    processedHtml = processedHtml.replace(/style="[^"]*position:[^"]*"/gi, '');
    processedHtml = processedHtml.replace(/style="[^"]*z-index:[^"]*"/gi, '');
    
    console.log("HTML processing complete");
    return processedHtml;
  };

  return (
    <div className="space-y-2">
      {!hideSubject && (
        <div className="space-y-1">
          <h2 className="text-xl font-semibold">{email.subject}</h2>
        </div>
      )}
      <div className="flex justify-between text-sm text-gray-500">
        <span>{email.sender}</span>
        <span>{email.date}</span>
      </div>
      
      {/* Attachments viewer at the top of the email content */}
      {email.attachments && email.attachments.length > 0 && (
        <AttachmentViewer attachments={email.attachments} />
      )}
      
      <ScrollArea className="h-[400px] border rounded-md">
        <div className="p-4">
          {email.htmlContent ? (
            <div className="email-content-wrapper" ref={contentRef}>
              <div
                className="prose max-w-none"
                lang="auto"
                dangerouslySetInnerHTML={{ 
                  __html: processHtmlContent(email.htmlContent)
                }}
              />
            </div>
          ) : (
            <div className="prose max-w-none whitespace-pre-wrap">
              {email.content || email.preview}
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

export default EmailViewer;
